import { AfterViewInit, Component, DoCheck, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { formatNumber } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { catchError, of, Subscription, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { ListViewRow } from '../../../core/model/list-view-row';
import { LocalDate } from '../../../core/model/local-date';
import { isMoneyBackend, isMoneyLike, Money } from '../../../core/model/money';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { LanguageService } from '../../../core/service/language.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { Location } from '@angular/common';
import { Article } from '../../model/article';
import {
  LeaseAgreementStatus,
  LeaseAgreementStatusToString,
  PropertyTaxCalculationBasisToString,
  PropertyTaxCalculationMethodToString,
  RentalChangesUnit,
  RentalChangesUnitToString,
} from '../../model/const/enums';
import {
  EMPTY_PROPERTY_TAX_RENTAL_CHANGES_RESPONSE,
  PropertyTaxLeaseAgreementChangeModel,
  PropertyTaxRentalChangeModel,
  PropertyTaxRentalObjectChangeModel,
  PropertyTaxRentalChangesResponseModel,
} from '../../model/property-tax-rental-change';
import { ArticleService } from '../../service/data/article.service';
import { PropertyTaxFeeChangeService } from '../../service/data/property-tax-fee-change.service';

@Component({
    selector: 'app-property-tax-rental-changes',
    templateUrl: './property-tax-rental-changes.component.html',
    styleUrls: ['./property-tax-rental-changes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PropertyTaxRentalChangesComponent implements AfterViewInit, DoCheck, OnDestroy, OnInit {
  constructor(
    private _articleData: ArticleService,
    private _cf: ColumnDefinitionFactory,
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _propertyTaxFeeChangeService: PropertyTaxFeeChangeService,
    private _location: Location,
    private _messageDispatcher: ComponentMessageDispatcher,
    private _messageService: MessageService,
    private _route: ActivatedRoute,
    private _languageService: LanguageService
  ) {
    this.fromDate = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().addMonths(1)).firstDayInMonth());
    this.missedPeriodFromDate = new LocalDate(LocalDate.today().year, 1, 1);
    this.missedPeriodToDate = LocalDate.fromDate(this.fromDate.clone().addDays(-1));

    this.form = this._fb.group({
      articles: this._fb.control([]),
      fromDate: this._fb.group({}),
      rentalObjects: this._fb.control([]),
      rentalChangeValue: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_VALUE', null),
      rentalChangeUnit: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_UNIT', RentalChangesUnit.PerYear),
      propertyTaxPerYearByPremises: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_VALUE_OBJECT', null),
      roundPeriodRent: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_ROUND_PERIOD_RENT', false),
      useRetroactiveRent: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_RETRO', false),
      missedPeriodFromDate: this._fb.group({}),
      missedPeriodToDate: this._fb.group({}),
      changeInvoiceText: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_CHANGE_INVOICETEXT', true),
      newInvoiceText: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_INVOICETEXT', 'Retroaktiv fastighetsskatt'),
      changeRetroactiveArticle: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_CHANGE_RETROACTIVE_ARTICLE', false),
      retroactiveMonths: this._fc.create('PROPERTY_TAX.FEE_CHANGE.RETROACTIVE_MONTHS', null),
    });

    this.articleColumnDefinitions = [
      this._cf.create('ARTICLE_LIST.CODE_COLUMN', 'code'),
      this._cf.create('ARTICLE_LIST.CAPTION_COLUMN', 'caption'),
    ];

    this.rentalChangeColumnDefinitions = this.toPropertyTaxLeaseAgreementColumns();
    this.objectRentalChangeColumnDefinitions = this.toPropertyTaxRentalObjectColumns();
    this.previewRentalChangeColumnDefinitions = this.toRentalChangePreviewColumns();
    this.previewClosedRentalChangeColumnDefinitions = this.toClosedLeasePreviewColumns();
    this.previewObjectRentalChangeColumnDefinitions = this.toRentalObjectPreviewColumns();
  }

  form: FormGroup;
  private _subscription: Subscription;
  articles: ListViewRow[];
  articleColumnDefinitions: ColumnDefinition[];

  get canChange(): boolean {
    return (
      (this.rentalChanges && this.rentalChanges.length > 0) ||
      (this.closedLeaseAgreementChanges && this.closedLeaseAgreementChanges.length > 0) ||
      (this.rentalObjectChanges && this.rentalObjectChanges.length > 0)
    );
  }

  fromDate: LocalDate;
  missedPeriodFromDate: LocalDate;
  missedPeriodToDate: LocalDate;

  get rentalChangeValue(): AbstractControl {
    return this.form.controls['rentalChangeValue'];
  }

  get rentalChangeUnit(): AbstractControl {
    return this.form.controls['rentalChangeUnit'];
  }

  get useRetroactiveRent(): AbstractControl {
    return this.form.controls['useRetroactiveRent'];
  }

  get changeInvoiceText(): AbstractControl {
    return this.form.controls['changeInvoiceText'];
  }

  get newInvoiceText(): AbstractControl {
    return this.form.controls['newInvoiceText'];
  }

  get changeRetroactiveArticle(): AbstractControl {
    return this.form.controls['changeRetroactiveArticle'];
  }

  get retroactiveMonths(): AbstractControl {
    return this.form.controls['retroactiveMonths'];
  }

  rentalChangeUnits = [
    { name: RentalChangesUnitToString(RentalChangesUnit.Persentage), value: RentalChangesUnit.Persentage },
    { name: RentalChangesUnitToString(RentalChangesUnit.PerSquareMeter), value: RentalChangesUnit.PerSquareMeter },
    { name: RentalChangesUnitToString(RentalChangesUnit.PerYear), value: RentalChangesUnit.PerYear },
  ];

  get roundPeriodRent(): AbstractControl {
    return this.form.controls['roundPeriodRent'];
  }

  isPreview: boolean;
  rentalChanges: any[];
  rentalObjectChanges: any[];
  rentalChangeColumnDefinitions: ColumnDefinition[];
  objectRentalChangeColumnDefinitions: ColumnDefinition[];
  previewRentalChangeColumnDefinitions: ColumnDefinition[];
  previewClosedRentalChangeColumnDefinitions: ColumnDefinition[];
  previewObjectRentalChangeColumnDefinitions: ColumnDefinition[];
  leaseAgreementChangeRows: ListViewRow[] = [];
  rentalObjectChangeRows: ListViewRow[] = [];
  private _selectedLeaseAgreementStatus = 0;
  filterDefinition?: FilterDefinitionItem[];
  defaultFilters: string[] = ['status'];
  private _allLeaseAgreementChangeRows: ListViewRow[] = [];
  private _allRentalObjectChangeRows: ListViewRow[] = [];
  private readonly _activeLeaseAgreementStatuses: LeaseAgreementStatus[] = [
    LeaseAgreementStatus.Coming,
    LeaseAgreementStatus.Valid,
    LeaseAgreementStatus.Terminated,
  ];

  ngAfterViewInit() {
    // app-filter builds its controls in ngOnChanges and expects filters to arrive after its own view exists.
    setTimeout(() => {
      this.filterDefinition = this.createLeaseAgreementFilterDefinition();
    });
  }

  cancel() {
    this.rentalChanges = [];
    this.closedLeaseAgreementChanges = [];
    this.rentalObjectChanges = [];
    this._pendingPayload = null;
    this.isPreview = false;
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }

    if (this._previewSub) {
      this._previewSub.unsubscribe();
    }
  }

  ngOnInit() {
    const includedIdsParam = this._route.snapshot.queryParamMap.getAll('includedIds');
    const includedIds =
      includedIdsParam && includedIdsParam.length
        ? includedIdsParam
        : (this._route.snapshot.queryParamMap.get('includedIds') || '').split(',').filter(x => !!x);

    const propertyIds = (includedIds || []).filter(id => !!id);

    this.form.controls['rentalObjects'].setValue([]);

    const source$ = propertyIds.length
      ? this._propertyTaxFeeChangeService.getPropertyTaxRentalChanges(propertyIds)
      : of(EMPTY_PROPERTY_TAX_RENTAL_CHANGES_RESPONSE);

    source$
      .pipe(
        tap(response => {
          const data = response || EMPTY_PROPERTY_TAX_RENTAL_CHANGES_RESPONSE;
          const suggestedFromDateRaw = this.getSourceValue<any>(data, 'suggestedFromDate', 'SuggestedFromDate');
          const suggestedFromDate = this.toLocalDate(suggestedFromDateRaw);
          if (suggestedFromDate) {
            this.applySuggestedFromDate(suggestedFromDate);
          }

          const leaseAgreementDtos = this.getDtoArray<PropertyTaxLeaseAgreementChangeModel>(
            data,
            'propertyTaxLeaseAgreementChangeModels',
            'PropertyTaxLeaseAgreementChangeModels'
          );
          const rentalObjectDtos = this.getDtoArray<PropertyTaxRentalObjectChangeModel>(
            data,
            'propertyTaxRentalObjectChangeModels',
            'PropertyTaxRentalObjectChangeModels'
          );

          const leaseAgreementChanges = this.toLeaseAgreementChangeModels(leaseAgreementDtos);
          const rentalObjectChanges = this.toRentalObjectChangeModels(rentalObjectDtos);

          this.rentalChanges = leaseAgreementChanges;
          this.rentalObjectChanges = rentalObjectChanges;
          this._pendingPayload = null;
          this._allLeaseAgreementChangeRows = this.toRowsWithSelectionState(leaseAgreementChanges, true);
          this._allRentalObjectChangeRows = this.toRowsWithSelectionState(rentalObjectChanges, false);
          this.applyLeaseAgreementStatusFilter(this._selectedLeaseAgreementStatus);
          this.rentalObjectChangeRows = [...this._allRentalObjectChangeRows];
          this.isPreview = false;
        }),
        catchError(error => {
          this._messageService.showError(error);
          throw error;
        })
      )
      .subscribe();
  }

  previewObjects: boolean = true;
  previewLeases: boolean = true;
  previewClosedLeases: boolean = true;
  showLeaseAgreementList: boolean = true;
  showRentalObjectList: boolean = true;
  closedLeaseAgreementChanges: any[] = [];

  private _previewSub: Subscription;
  private _pendingPayload: any = null;

  toggleLeaseAgreementList(): void {
    this.showLeaseAgreementList = !this.showLeaseAgreementList;
  }

  toggleRentalObjectList(): void {
    this.showRentalObjectList = !this.showRentalObjectList;
  }

  onLeaseAgreementFilterChange(filterState: any): void {
    const selectedStatus = Number(filterState?.status ?? 0);
    this._selectedLeaseAgreementStatus = Number.isNaN(selectedStatus) ? 0 : selectedStatus;
    this.applyLeaseAgreementStatusFilter(this._selectedLeaseAgreementStatus);
  }

  private ensureLocalDateGroup(controlName: string, date: LocalDate) {
    const ctrl = this.form?.controls?.[controlName];
    if (!ctrl || !(ctrl instanceof FormGroup)) return;

    const value: any = ctrl.value;
    const hasValue = value && value.year && value.month && value.day;
    if (hasValue) return;

    const ld = LocalDate.fromDate(date);
    this.form.setControl(
      controlName,
      this._fb.group({
        year: [ld?.year ?? null],
        month: [ld?.month ?? null],
        day: [ld?.day ?? null],
      })
    );
  }

  private setLocalDateGroupValue(controlName: string, date: LocalDate) {
    const ctrl = this.form?.controls?.[controlName];
    if (!ctrl || !(ctrl instanceof FormGroup)) return;

    const ld = LocalDate.fromDate(date);
    const nextValue = {
      year: ld?.year ?? null,
      month: ld?.month ?? null,
      day: ld?.day ?? null,
    };

    if (!ctrl.get('year') || !ctrl.get('month') || !ctrl.get('day')) {
      this.form.setControl(controlName, this._fb.group(nextValue));
      return;
    }

    ctrl.setValue(nextValue);
  }

  private unwrapPreviewRow(row: any): any {
    if (!row) return row;

    const source = row.dataSource ?? row.change ?? row.row ?? row.dto ?? row;
    if (!source) return source;

    return {
      ...source,
      leaseAgreementNumber: source.leaseAgreementNumber ?? source.LeaseAgreementNumber ?? null,
      rentalId: source.rentalId ?? source.RentalId ?? null,
      rentalObjectAddress: source.rentalObjectAddress ?? source.RentalObjectAddress ?? null,
      previousYearPropertyTax: source.previousYearPropertyTax ?? source.PreviousYearPropertyTax ?? null,
      newYearPropertyTax: source.newYearPropertyTax ?? source.NewYearPropertyTax ?? null,
      previousPeriodPropertyTax: source.previousPeriodPropertyTax ?? source.PreviousPeriodPropertyTax ?? null,
      newPeriodPropertyTax: source.newPeriodPropertyTax ?? source.NewPeriodPropertyTax ?? null,
      previousMissedPeriodPropertyTax: source.previousMissedPeriodPropertyTax ?? source.PreviousMissedPeriodPropertyTax ?? null,
      newMissedPeriodPropertyTax: source.newMissedPeriodPropertyTax ?? source.NewMissedPeriodPropertyTax ?? null,
      yearPropertyTaxDifference: source.yearPropertyTaxDifference ?? source.YearPropertyTaxDifference ?? null,
      retroactiveAmount: source.retroactiveAmount ?? source.RetroactiveAmount ?? null,
      nextInvoiceTotal: source.nextInvoiceTotal ?? source.NextInvoiceTotal ?? null,
    };
  }

  private toPropertyTaxByKeyMap(
    selectedChanges: PropertyTaxRentalChangeModel[],
    keySelector: (change: PropertyTaxRentalChangeModel) => string | null | undefined
  ): { [key: string]: number } {
    return (selectedChanges || []).reduce(
      (acc, change) => {
        const key = keySelector(change);
        if (!key || !change?.taxPerYear) return acc;
        acc[key] = change.taxPerYear.amount;
        return acc;
      },
      {} as { [key: string]: number }
    );
  }

  private toPropertyTaxArticleByKeyMap(
    selectedChanges: PropertyTaxRentalChangeModel[],
    keySelector: (change: PropertyTaxRentalChangeModel) => string | null | undefined
  ): { [key: string]: string } {
    return (selectedChanges || []).reduce(
      (acc, change) => {
        const key = keySelector(change);
        if (!key || !change?.propertyTaxArticle?.id) return acc;
        acc[key] = change.propertyTaxArticle.id;
        return acc;
      },
      {} as { [key: string]: string }
    );
  }

  ngDoCheck() {
    this.syncSelectionState(this._allLeaseAgreementChangeRows, true);
    this.syncSelectionState(this._allRentalObjectChangeRows, false);
  }

  private applySuggestedFromDate(date: LocalDate) {
    this.fromDate = LocalDate.fromDate(date);
    this.missedPeriodToDate = LocalDate.fromDate(this.fromDate.clone().addDays(-1));
    this.setLocalDateGroupValue('fromDate', this.fromDate);
    this.setLocalDateGroupValue('missedPeriodToDate', this.missedPeriodToDate);
  }

  private toLocalDate(value: any): LocalDate | null {
    if (!value) return null;

    const parsed = LocalDate.fromDate(value);
    if (parsed?.hasValue()) return parsed;

    const year = value.year ?? value.Year;
    const month = value.month ?? value.Month;
    const day = value.day ?? value.Day;
    if (year && month && day) {
      return new LocalDate(year, month, day);
    }

    return null;
  }

  private getSourceValue<T = any>(source: any, ...keys: string[]): T | undefined {
    if (!source) return undefined;

    for (const key of keys) {
      if (key in Object(source)) {
        return source[key] as T;
      }
    }

    return undefined;
  }

  private getDtoArray<T>(source: any, ...keys: string[]): T[] {
    const value = this.getSourceValue<any>(source, ...keys);
    if (Array.isArray(value)) {
      return value as T[];
    }

    return Array.isArray(source) ? (source as T[]) : [];
  }

  private toLeaseAgreementStatus(value: any): LeaseAgreementStatus | null {
    if (!this.hasValue(value)) return null;

    if (typeof value === 'number') {
      return Number.isFinite(value) ? (value as LeaseAgreementStatus) : null;
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        return numericValue as LeaseAgreementStatus;
      }

      const enumKey = value.trim();
      const resolvedStatus =
        (LeaseAgreementStatus as any)[enumKey] ?? (LeaseAgreementStatus as any)[enumKey.charAt(0).toUpperCase() + enumKey.slice(1)];

      return typeof resolvedStatus === 'number' ? (resolvedStatus as LeaseAgreementStatus) : null;
    }

    return null;
  }

  private toNumericValue(value: any): number | null {
    if (!this.hasValue(value)) return null;

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private toBooleanValue(value: any): boolean | null {
    if (!this.hasValue(value)) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }

    return null;
  }

  private toArticle(value: any): Article | null {
    if (!value) return null;
    if (value instanceof Article) return value;

    return new Article({
      id: this.getSourceValue(value, 'id', 'Id'),
      caption: this.getSourceValue(value, 'caption', 'Caption'),
      code: this.getSourceValue(value, 'code', 'Code'),
      currencyCode: this.getSourceValue(value, 'currencyCode', 'CurrencyCode'),
      price: this.getSourceValue(value, 'price', 'Price'),
      vatPercent: this.getSourceValue(value, 'vatPercent', 'VatPercent'),
      isPropertyTax: this.getSourceValue(value, 'isPropertyTax', 'IsPropertyTax'),
      isRetroactiveForPropertyTax: this.getSourceValue(value, 'isRetroactiveForPropertyTax', 'IsRetroactiveForPropertyTax'),
    });
  }

  private toTaxTypeCode(value: any) {
    if (!value) return null;

    return {
      propertyType: this.getSourceValue(value, 'propertyType', 'PropertyType'),
      typeCode: this.getSourceValue(value, 'typeCode', 'TypeCode'),
      name: this.getSourceValue(value, 'name', 'Name'),
      percentage: this.getSourceValue(value, 'percentage', 'Percentage'),
    };
  }

  private toRowsWithSelectionState(changes: PropertyTaxRentalChangeModel[], requireLeaseAgreement: boolean): ListViewRow[] {
    return (changes || []).map(change => {
      const row = new ListViewRow(change);
      const disabled = !this.isSelectableTaxChange(change, requireLeaseAgreement);
      (row as any).disableSelection = disabled;
      (row.dataSource as any).disableSelection = disabled;
      row.highlight = disabled;

      if (disabled) {
        row.selected = false;
      }
      return row;
    });
  }

  private createLeaseAgreementFilterDefinition(): FilterDefinitionItem[] {
    return [
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.STATUS',
        'status',
        this._selectedLeaseAgreementStatus,
        [
          { caption: 'ALL_TEXT', value: 0 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_ACTIVE', value: 9 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_DRAFT', value: LeaseAgreementStatus.Draft },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_COMING', value: LeaseAgreementStatus.Coming },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VALID', value: LeaseAgreementStatus.Valid },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED', value: LeaseAgreementStatus.Terminated },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED', value: LeaseAgreementStatus.Expired },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VOIDED', value: LeaseAgreementStatus.Voided },
        ],
        true
      ),
    ];
  }

  private applyLeaseAgreementStatusFilter(selectedStatus: number): void {
    if (!selectedStatus) {
      this.leaseAgreementChangeRows = [...this._allLeaseAgreementChangeRows];
      return;
    }

    this.leaseAgreementChangeRows = this._allLeaseAgreementChangeRows.filter(row =>
      this.matchesLeaseAgreementStatus(row?.dataSource as PropertyTaxRentalChangeModel, selectedStatus)
    );
  }

  private matchesLeaseAgreementStatus(change: PropertyTaxRentalChangeModel | null | undefined, selectedStatus: number): boolean {
    if (!change?.leaseAgreementId || !change.status) return false;

    if (selectedStatus === 9) {
      return this._activeLeaseAgreementStatuses.includes(change.status);
    }

    return change.status === selectedStatus;
  }

  private syncSelectionState(rows: ListViewRow[], requireLeaseAgreement: boolean) {
    (rows || []).forEach(row => {
      const change = row?.dataSource as PropertyTaxRentalChangeModel;
      const disabled = !this.isSelectableTaxChange(change, requireLeaseAgreement);

      (row as any).disableSelection = disabled;
      (row.dataSource as any).disableSelection = disabled;
      row.highlight = disabled;

      if (disabled) {
        row.selected = false;
      }
    });
  }

  private isSelectionDisabledRow(row: ListViewRow): boolean {
    return !!((row as any)?.disableSelection || (row?.dataSource as any)?.disableSelection);
  }

  private isClosedLeaseAgreementStatus(status: LeaseAgreementStatus | null | undefined): boolean {
    return status === LeaseAgreementStatus.Terminated || status === LeaseAgreementStatus.Expired;
  }

  private isSelectableTaxChange(change: PropertyTaxRentalChangeModel, requireLeaseAgreement: boolean): boolean {
    if (!change) return false;
    if (requireLeaseAgreement && !change.leaseAgreementId) return false;
    if (requireLeaseAgreement && change.status === LeaseAgreementStatus.Draft) return false;

    if (!change.propertyId || !change.premisesId || !change.rentalId) return false;
    if (!change.propertyTaxArticle?.id) return false;
    if (!change.taxPerYear || !this.hasValue(change.taxPerYear.amount)) return false;
    if (!change.taxPerMonth || !this.hasValue(change.taxPerMonth.amount)) return false;
    if (!change.taxPerYearInclVat || !this.hasValue(change.taxPerYearInclVat.amount)) return false;
    if (!change.taxPerMonthInclVat || !this.hasValue(change.taxPerMonthInclVat.amount)) return false;

    if (!this.hasValue(change.taxPercentage)) return false;
    if (!this.hasValue(change.forDebit)) return false;
    if (!this.hasValue(change.share)) return false;
    if (!change.assessmentYear) return false;

    if (!change.taxTypeCode || change.taxTypeName === 'N/A') return false;
    if (!change.taxPercentageDisplay || change.taxPercentageDisplay === 'N/A') return false;
    if (!change.shareDisplay || change.shareDisplay === 'N/A') return false;
    if (!change.calculationBasis || change.calculationBasis === 'N/A') return false;
    if (!change.calculationMethod || change.calculationMethod === 'N/A') return false;
    if (requireLeaseAgreement && !change.invoiceableFrom && !this.isClosedLeaseAgreementStatus(change.status)) return false;
    if (this.form?.controls?.['useRetroactiveRent']?.value && !change.propertyTaxRetroactiveArticle?.id) return false;

    return true;
  }

  private getSelectedTaxChanges(rows: ListViewRow[]): PropertyTaxRentalChangeModel[] {
    return (rows || []).filter(x => x.selected && !this.isSelectionDisabledRow(x)).map(x => x.dataSource as PropertyTaxRentalChangeModel);
  }

  private toDistinctIds(values: (string | null | undefined)[]): string[] {
    return [...new Set((values || []).filter(x => !!x))] as string[];
  }

  private getSelectionIds(
    selectedLeaseAgreementChanges: PropertyTaxRentalChangeModel[],
    selectedRentalObjectChanges: PropertyTaxRentalChangeModel[]
  ) {
    const selectedLeaseAgreementScopedChanges = selectedLeaseAgreementChanges.filter(x => !!x.leaseAgreementId);
    const selectedLeasePremisesIds = this.toDistinctIds(selectedLeaseAgreementScopedChanges.map(x => x.premisesId));
    const selectedLeaseAgreementIds = this.toDistinctIds(selectedLeaseAgreementScopedChanges.map(x => x.leaseAgreementId));
    const selectedObjectPremisesIds = this.toDistinctIds(selectedRentalObjectChanges.map(x => x.premisesId));
    const selectedPremisesIds = this.toDistinctIds([...selectedLeasePremisesIds, ...selectedObjectPremisesIds]);

    return {
      selectedLeaseAgreementScopedChanges,
      selectedLeasePremisesIds,
      selectedLeaseAgreementIds,
      selectedObjectPremisesIds,
      selectedPremisesIds,
    };
  }

  private buildPropertyTaxMaps(
    selectedLeaseAgreementScopedChanges: PropertyTaxRentalChangeModel[],
    selectedRentalObjectChanges: PropertyTaxRentalChangeModel[]
  ) {
    const propertyTaxPerYearByLeaseAgreementPremises = this.toPropertyTaxByKeyMap(
      selectedLeaseAgreementScopedChanges,
      x => x.leaseAgreementId
    );
    const propertyTaxPerYearByRentalObjectPremises = this.toPropertyTaxByKeyMap(selectedRentalObjectChanges, x => x.premisesId);
    const propertyTaxPerYearByPremises = {
      ...propertyTaxPerYearByRentalObjectPremises,
    };
    const propertyTaxArticleByLeaseAgreementPremises = this.toPropertyTaxArticleByKeyMap(
      selectedLeaseAgreementScopedChanges,
      x => x.leaseAgreementId
    );
    const propertyTaxArticleByRentalObjectPremises = this.toPropertyTaxArticleByKeyMap(selectedRentalObjectChanges, x => x.premisesId);

    return {
      propertyTaxPerYearByPremises,
      propertyTaxPerYearByLeaseAgreementPremises,
      propertyTaxPerYearByRentalObjectPremises,
      propertyTaxArticleByLeaseAgreementPremises,
      propertyTaxArticleByRentalObjectPremises,
    };
  }

  private buildSelectionPayload(): {
    payload: any;
    hasLeaseSelections: boolean;
    hasObjectSelections: boolean;
  } | null {
    const selectedLeaseAgreementChanges = this.getSelectedTaxChanges(this._allLeaseAgreementChangeRows);
    const selectedRentalObjectChanges = this.getSelectedTaxChanges(this._allRentalObjectChangeRows);

    if (selectedLeaseAgreementChanges.length === 0 && selectedRentalObjectChanges.length === 0) {
      this._messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
      return null;
    }

    const selectionIds = this.getSelectionIds(selectedLeaseAgreementChanges, selectedRentalObjectChanges);

    if (selectionIds.selectedPremisesIds.length === 0) {
      this._messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
      return null;
    }

    const taxMaps = this.buildPropertyTaxMaps(selectionIds.selectedLeaseAgreementScopedChanges, selectedRentalObjectChanges);

    const articleIds = [
      ...new Set([...selectedLeaseAgreementChanges, ...selectedRentalObjectChanges].map(x => x.propertyTaxArticle?.id).filter(x => !!x)),
    ];

    const selectedAssessmentYears = [...selectedLeaseAgreementChanges, ...selectedRentalObjectChanges]
      .map(x => (x?.assessmentYear ? LocalDate.fromDate(x.assessmentYear) : null))
      .filter((x): x is LocalDate => !!x && x.hasValue());

    let earliestAssessmentYear: LocalDate | null = null;
    selectedAssessmentYears.forEach(date => {
      if (!earliestAssessmentYear || LocalDate.GreaterThan(earliestAssessmentYear, date)) {
        earliestAssessmentYear = date;
      }
    });

    const formMissedPeriodFromDate = this.toLocalDate((this.form?.value as any)?.missedPeriodFromDate);
    const fallbackMissedPeriodFromDate =
      formMissedPeriodFromDate ?? (this.missedPeriodFromDate?.hasValue() ? this.missedPeriodFromDate : null);

    let resolvedMissedPeriodFromDate = earliestAssessmentYear;
    if (
      fallbackMissedPeriodFromDate &&
      (!resolvedMissedPeriodFromDate || LocalDate.GreaterThan(resolvedMissedPeriodFromDate, fallbackMissedPeriodFromDate))
    ) {
      resolvedMissedPeriodFromDate = fallbackMissedPeriodFromDate;
    }

    const missedPeriodFromDate = resolvedMissedPeriodFromDate
      ? {
          year: resolvedMissedPeriodFromDate.year,
          month: resolvedMissedPeriodFromDate.month,
          day: resolvedMissedPeriodFromDate.day,
        }
      : null;

    const hasLeaseSelections = selectionIds.selectedLeasePremisesIds.length > 0;
    const hasObjectSelections = selectionIds.selectedObjectPremisesIds.length > 0;

    return {
      hasLeaseSelections,
      hasObjectSelections,
      payload: {
        ...this.form.value,
        missedPeriodFromDate,
        rentalObjects: selectionIds.selectedPremisesIds,
        leaseAgreements: selectionIds.selectedLeaseAgreementIds,
        leaseAgreementRentalObjects: selectionIds.selectedLeasePremisesIds,
        rentalObjectRentalObjects: selectionIds.selectedObjectPremisesIds,
        propertyTaxPerYearByPremises: taxMaps.propertyTaxPerYearByPremises,
        propertyTaxPerYearByLeaseAgreementPremises: taxMaps.propertyTaxPerYearByLeaseAgreementPremises,
        propertyTaxPerYearByRentalObjectPremises: taxMaps.propertyTaxPerYearByRentalObjectPremises,
        propertyTaxArticleByLeaseAgreementPremises: taxMaps.propertyTaxArticleByLeaseAgreementPremises,
        propertyTaxArticleByRentalObjectPremises: taxMaps.propertyTaxArticleByRentalObjectPremises,
        articles: articleIds,
      },
    };
  }

  preview() {
    if (this.form.invalid) return;

    this.ensureLocalDateGroup('fromDate', this.fromDate);
    this.ensureLocalDateGroup('missedPeriodFromDate', this.missedPeriodFromDate);
    this.ensureLocalDateGroup('missedPeriodToDate', this.missedPeriodToDate);

    const selectionPayload = this.buildSelectionPayload();
    if (!selectionPayload) return;
    this._pendingPayload = selectionPayload.payload;

    if (this._previewSub) {
      this._previewSub.unsubscribe();
      this._previewSub = null;
    }

    this._previewSub = this._propertyTaxFeeChangeService.previewPropertyTaxChanges(selectionPayload.payload).subscribe(
      data => {
        const previewData: any = data;
        let leaseAgreementChanges = ((previewData?.leaseAgreementChanges ?? previewData?.LeaseAgreementChanges) || []).map(x =>
          this.unwrapPreviewRow(x)
        );
        let closedLeaseAgreementChanges = (
          (previewData?.closedLeaseAgreementChanges ?? previewData?.ClosedLeaseAgreementChanges) ||
          []
        ).map(x => this.unwrapPreviewRow(x));
        const rentalObjectChanges = ((previewData?.rentalObjectChanges ?? previewData?.RentalObjectChanges) || []).map(x =>
          this.unwrapPreviewRow(x)
        );

        this.previewLeases = leaseAgreementChanges.length > 0;
        this.previewClosedLeases = closedLeaseAgreementChanges.length > 0;
        this.previewObjects = rentalObjectChanges.length > 0;
        this.isPreview = true;
        this.rentalChanges = leaseAgreementChanges;
        this.closedLeaseAgreementChanges = closedLeaseAgreementChanges;
        this.rentalObjectChanges = rentalObjectChanges;
      },
      error => this._messageService.showError(error)
    );
  }

  working: boolean;

  private toPropertyTaxLeaseAgreementColumns(): ColumnDefinition[] {
    return [
      this._cf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cf.create('COMMON.BUILDING', 'buildingName'),
      this._cf.create('COMMON.TENANT', 'tenantName'),
      this._cf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cf.createBoolean('PROPERTY_TAX.FEE_CHANGE.FOR_DEBIT', 'forDebit'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.TAX_PERCENTAGE', 'taxPercentageDisplay'),
      this._cf.create('PROPERTY_TAX.TYPE.PROPERTY_TAX_TYPE', 'taxTypeName'),
      this._cf.createDate('FEE_CHANGE.CHANGE_FROM', 'assessmentYear'),
      this._cf.createDate('PROPERTY_TAX.FEE_CHANGE.INVOICEABLE_FROM', 'invoiceableFrom'),
      this._cf.createDecimal('PROPERTY_TAX.FEE_CHANGE.SURFACE', 'surface'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.SHARE', 'shareDisplay'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.CALCULATION_BASIS', 'calculationBasis'),
      this._cf.create('PROPERTY_TAX.CALCULATION_METHOD', 'calculationMethod'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_YEAR', 'taxPerYear'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_MONTH', 'taxPerMonth'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_YEAR_INCL_VAT', 'taxPerYearInclVat'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_MONTH_INCL_VAT', 'taxPerMonthInclVat'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.PROPERTY_TAX_ARTICLE', 'propertyTaxArticleCaption'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.PROPERTY_TAX_RETROACTIVE_ARTICLE', 'propertyTaxRetroactiveArticleCaption'),
      this._cf.createDecimal('PROPERTY_TAX.FEE_CHANGE.MISSED_PERIOD_DAYS', 'missedPeriodDays'),
      // this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.RETROACTIVE_AMOUNT', 'retroactiveAmount'),
    ];
  }

  private toPropertyTaxRentalObjectColumns(): ColumnDefinition[] {
    return [
      this._cf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cf.create('COMMON.BUILDING', 'buildingName'),
      this._cf.createBoolean('PROPERTY_TAX.FEE_CHANGE.FOR_DEBIT', 'forDebit'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.TAX_PERCENTAGE', 'taxPercentageDisplay'),
      this._cf.create('PROPERTY_TAX.TYPE.PROPERTY_TAX_TYPE', 'taxTypeName'),
      this._cf.createDate('FEE_CHANGE.CHANGE_FROM', 'assessmentYear'),
      this._cf.createDecimal('PROPERTY_TAX.FEE_CHANGE.SURFACE', 'surface'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.SHARE', 'shareDisplay'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.CALCULATION_BASIS', 'calculationBasis'),
      this._cf.create('PROPERTY_TAX.CALCULATION_METHOD', 'calculationMethod'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_YEAR', 'taxPerYear'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_MONTH', 'taxPerMonth'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_YEAR_INCL_VAT', 'taxPerYearInclVat'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.TAX_PER_MONTH_INCL_VAT', 'taxPerMonthInclVat'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.PROPERTY_TAX_ARTICLE', 'propertyTaxArticleCaption'),
      this._cf.create('PROPERTY_TAX.FEE_CHANGE.PROPERTY_TAX_RETROACTIVE_ARTICLE', 'propertyTaxRetroactiveArticleCaption'),
    ];
  }

  private toRentalChangePreviewColumns(): ColumnDefinition[] {
    return [
      this._cf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'rentalObjectAddress'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.PREVIOUS_PERIOD_PROPERTY_TAX', 'previousPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.NEW_PERIOD_PROPERTY_TAX', 'newPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.PREVIOUS_YEAR_PROPERTY_TAX', 'previousYearPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.NEW_YEAR_PROPERTY_TAX', 'newYearPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.DIFFERENCE', 'yearPropertyTaxDifference'),
    ];
  }

  private toClosedLeasePreviewColumns(): ColumnDefinition[] {
    return [
      this._cf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.PREVIOUS_MISSED_PERIOD_PROPERTY_TAX', 'previousMissedPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.NEW_MISSED_PERIOD_PROPERTY_TAX', 'newMissedPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.RETROACTIVE_AMOUNT', 'retroactiveAmount'),
    ];
  }

  private toRentalObjectPreviewColumns(): ColumnDefinition[] {
    return [
      this._cf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'rentalObjectAddress'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.PREVIOUS_PERIOD_PROPERTY_TAX', 'previousPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.NEW_PERIOD_PROPERTY_TAX', 'newPeriodPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.PREVIOUS_YEAR_PROPERTY_TAX', 'previousYearPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.NEW_YEAR_PROPERTY_TAX', 'newYearPropertyTax'),
      this._cf.createMoney('PROPERTY_TAX.FEE_CHANGE.DIFFERENCE', 'yearPropertyTaxDifference'),
    ];
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined;
  }

  private formatPercent(value: number): string {
    if (!this.hasValue(value)) return null;
    const locale = LanguageService.getCultureLang() || 'sv-SE';
    return `${formatNumber(value * 100, locale, '1.1-2')} %`;
  }

  private toLeaseAgreementChangeModels(dtos: PropertyTaxLeaseAgreementChangeModel[] | null | undefined): PropertyTaxRentalChangeModel[] {
    return (dtos || []).map(dto => this.toPropertyTaxModel(dto));
  }

  private toRentalObjectChangeModels(dtos: PropertyTaxRentalObjectChangeModel[] | null | undefined): PropertyTaxRentalChangeModel[] {
    return (dtos || []).map(dto => this.toPropertyTaxModel(dto));
  }

  private toPropertyTaxModel(
    dto: Partial<PropertyTaxLeaseAgreementChangeModel | PropertyTaxRentalObjectChangeModel> | null | undefined
  ): PropertyTaxRentalChangeModel {
    const toMoney = (value: any): Money => {
      if (!value) return null;
      if (value instanceof Money) return value;
      if (isMoneyBackend(value)) return new Money(value.currency.isoCode, value.amount);
      if (isMoneyLike(value)) return new Money(value.currencyCode, value.amount);
      return null;
    };

    const model: any = dto || {};
    const leaseAgreementModel = model as Partial<PropertyTaxLeaseAgreementChangeModel>;
    const status = this.toLeaseAgreementStatus(this.getSourceValue(model, 'status', 'Status'));
    const taxPercentage = this.toNumericValue(this.getSourceValue(model, 'taxPercentage', 'TaxPercentage'));
    const share = this.toNumericValue(this.getSourceValue(model, 'share', 'Share'));
    const surface = this.toNumericValue(this.getSourceValue(model, 'surface', 'Surface')) ?? 0;
    const taxTypeCode = this.toTaxTypeCode(this.getSourceValue(model, 'taxTypeCode', 'TaxTypeCode'));
    const propertyTaxArticle = this.toArticle(this.getSourceValue(model, 'propertyTaxArticle', 'PropertyTaxArticle'));
    const propertyTaxRetroactiveArticle = this.toArticle(
      this.getSourceValue(model, 'propertyTaxRetroactiveArticle', 'PropertyTaxRetroactiveArticle')
    );
    const calculationBasis = this.getSourceValue(model, 'calculationBasis', 'CalculationBasis');
    const calculationMethod = this.getSourceValue(model, 'calculationMethod', 'CalculationMethod');
    const missedPeriodDays = this.toNumericValue(this.getSourceValue(leaseAgreementModel, 'missedPeriodDays', 'MissedPeriodDays'));

    return {
      propertyId: this.getSourceValue(model, 'propertyId', 'PropertyId'),
      premisesId: this.getSourceValue(model, 'premisesId', 'PremisesId'),
      leaseAgreementId: this.getSourceValue(model, 'leaseAgreementId', 'LeaseAgreementId'),
      buildingName: this.getSourceValue(model, 'buildingName', 'BuildingName'),
      status,
      statusCaption: status ? LeaseAgreementStatusToString(status) : null,
      taxPercentage,
      taxPercentageDisplay: this.hasValue(taxPercentage) ? this.formatPercent(taxPercentage) : 'N/A',
      rentalId: this.getSourceValue(model, 'rentalId', 'RentalId'),
      taxTypeCode,
      taxTypeName: taxTypeCode ? taxTypeCode.typeCode + ', ' + taxTypeCode.name : 'N/A',
      tenantName: this.getSourceValue(model, 'tenantName', 'TenantName'),
      forDebit: this.toBooleanValue(this.getSourceValue(model, 'forDebit', 'ForDebit')),
      assessmentYear: this.toLocalDate(this.getSourceValue(model, 'assessmentYear', 'AssessmentYear')),
      invoiceableFrom: this.toLocalDate(this.getSourceValue(model, 'invoiceableFrom', 'InvoiceableFrom')),
      surface,
      share,
      shareDisplay: this.hasValue(share) ? this.formatPercent(share) : 'N/A',
      calculationBasis: this.hasValue(calculationBasis)
        ? this._languageService.getText(PropertyTaxCalculationBasisToString(calculationBasis))
        : 'N/A',
      calculationMethod: this.hasValue(calculationMethod)
        ? this._languageService.getText(PropertyTaxCalculationMethodToString(calculationMethod))
        : 'N/A',
      taxPerYear: toMoney(this.getSourceValue(model, 'taxPerYear', 'TaxPerYear')),
      taxPerMonth: toMoney(this.getSourceValue(model, 'taxPerMonth', 'TaxPerMonth')),
      taxPerYearInclVat: toMoney(this.getSourceValue(model, 'taxPerYearInclVat', 'TaxPerYearInclVat')),
      taxPerMonthInclVat: toMoney(this.getSourceValue(model, 'taxPerMonthInclVat', 'TaxPerMonthInclVat')),
      propertyTaxArticle,
      propertyTaxArticleCaption: propertyTaxArticle?.caption ?? 'N/A',
      propertyTaxRetroactiveArticle,
      propertyTaxRetroactiveArticleCaption: propertyTaxRetroactiveArticle?.caption ?? 'N/A',
      missedPeriodDays,
      retroactiveAmount: toMoney(this.getSourceValue(leaseAgreementModel, 'retroactiveAmount', 'RetroactiveAmount')),
    };
  }

  save() {
    if (this.working) return;
    if (!this._pendingPayload) {
      this._messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
      return;
    }

    this.working = true;

    this._propertyTaxFeeChangeService.changePropertyTax(this._pendingPayload).subscribe(
      () => this._location.back(),
      error => {
        this.working = false;
        this._messageService.showError(error);
      }
    );
  }
}
